import {
  BILLING_PLAN_RECORDING_FEATURES,
  type BillingLookupKey,
  type BillingPlan,
  type BillingPlanId,
} from "@/consts/billing"
import type { PlanBillingCycle } from "@/pages/LandingPage/sections/Plans/plansData"

import { httpClient } from "./httpClient"
import { toUnixMilliseconds } from "./utils"

declare global {
  interface Window {
    tolt_referral?: string
  }
}

export type { BillingLookupKey } from "@/consts/billing"

export type BillingInterval = "" | PlanBillingCycle

export type BillingProvider = "" | "stripe" | "apple" | "google"

export type BillingStatus =
  | ""
  | "active"
  | "trialing"
  | "past_due"
  | "canceled"
  | "incomplete"
  | "incomplete_expired"
  | "unpaid"
  | "inactive"

export type RecordingAccessState = "active" | "grace" | "expired"

export const parseRecordingAccessState = (
  value?: string | null,
): RecordingAccessState | undefined => {
  if (value === "active" || value === "grace" || value === "expired") {
    return value
  }
  return undefined
}

interface BillingPlanRaw extends Omit<BillingPlan, "prices"> {
  prices: {
    monthly: number | null
    yearly: number | null
  }
  seats?: {
    included: number
    min: number
    max: number
    creditsPerSeat: number
    addSeatPrices: {
      monthly: number | null
      yearly: number | null
    }
    addSeatLookupKeys: {
      monthly: string | null
      yearly: string | null
    }
  }
}

type BillingPlansResponse = BillingPlanRaw[] | { plans?: BillingPlanRaw[] }

const centsToAmount = (value: number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null
  }

  return value / 100
}

const isNonNullNumber = (v: unknown): v is number =>
  v !== null && v !== undefined && typeof v === "number" && Number.isFinite(v)

const hasValidSeats = (
  seats: BillingPlanRaw["seats"],
): seats is NonNullable<BillingPlanRaw["seats"]> =>
  !!seats &&
  isNonNullNumber(seats.included) &&
  isNonNullNumber(seats.min) &&
  isNonNullNumber(seats.max) &&
  isNonNullNumber(seats.creditsPerSeat) &&
  isNonNullNumber(seats.addSeatPrices?.monthly) &&
  isNonNullNumber(seats.addSeatPrices?.yearly)

const normalizePlan = (raw: BillingPlanRaw): BillingPlan => ({
  ...raw,
  prices: {
    monthly: centsToAmount(raw.prices?.monthly),
    yearly: centsToAmount(raw.prices?.yearly),
  },
  seats: hasValidSeats(raw.seats)
    ? {
        ...raw.seats,
        addSeatPrices: {
          monthly: centsToAmount(raw.seats.addSeatPrices.monthly)!,
          yearly: centsToAmount(raw.seats.addSeatPrices.yearly)!,
        },
      }
    : undefined,
  features: {
    ...raw.features,
    recording:
      raw.features?.recording ??
      BILLING_PLAN_RECORDING_FEATURES[raw.id].recording,
    recording_storage_bytes:
      raw.features?.recording_storage_bytes ??
      BILLING_PLAN_RECORDING_FEATURES[raw.id].recording_storage_bytes,
  },
})

export interface BillingSummaryInvoice {
  id: string
  number?: string
  description?: string
  descriptionKey?: string
  descriptionParams?: InvoiceDescriptionParams
  amountCents: number
  currency: string
  status: string
  hostedUrl?: string
  pdfUrl?: string
  periodStart?: number
  periodEnd?: number
  date: number
}

export interface InvoiceDescriptionParams {
  credits?: string
  seats?: number
  price?: string
  interval?: string
  plan?: string
  date?: string
}

interface BillingSummaryInvoiceRaw extends Omit<
  BillingSummaryInvoice,
  "date" | "periodStart" | "periodEnd" | "descriptionKey" | "descriptionParams"
> {
  descriptionKey?: string | null
  descriptionParams?: InvoiceDescriptionParams | null
  periodStart?: number | string | null
  periodEnd?: number | string | null
  date: number | string
}

export type AutoReloadError =
  | "need_card_confirmation"
  | "monthly_limit_reached"
  | "payment_failed"

export interface BillingCreditsPackPricingView {
  packSize: number
  amountCents: number
  currency: string
  pricePerCredit: number
}

interface BillingCreditsPackPricingResponse {
  plan: string
  packs: BillingCreditsPackPricingView[]
}

interface BillingCreditsPackCheckoutRequest {
  packSize: number
  successPath: string
  cancelPath: string
}

interface BillingSummaryRaw {
  plan?: BillingPlanId
  interval?: BillingInterval
  status?: BillingStatus
  provider?: string | null
  currentPeriodEnd?: number
  creditsRenewAt?: number | string | null
  cancelAtPeriodEnd?: boolean
  pendingPlan?: string
  pendingEffectiveAt?: number | null
  manageBillingAvailable?: boolean
  recentInvoices?: BillingSummaryInvoiceRaw[]
  tariffCredits: number
  usage: {
    totalTariffCredits: number
    spentToSummary: number
    spentToTranscription: number
    spentToAskAi: number
    extraCredits: number
  }
  cardLastFour?: string
  cardBrand?: string
  cardExpMonth?: number
  cardExpYear?: number
  cardLinkEmail?: string
  autoReloadError?: AutoReloadError
  recordingAccessState?: string
  recordingGraceEndsAt?: number | string | null
}

export interface BillingSummary {
  plan: BillingPlanId
  interval: BillingInterval
  status: BillingStatus
  provider: BillingProvider
  currentPeriodEnd?: number
  creditsRenewAt: number | null
  cancelAtPeriodEnd: boolean
  pendingPlan: string
  pendingEffectiveAt: number | null
  manageBillingAvailable: boolean
  recentInvoices: BillingSummaryInvoice[]
  usage: BillingUsageSnapshot
  cardLastFour?: string
  cardBrand?: string
  cardExpMonth?: number
  cardExpYear?: number
  cardLinkEmail?: string
  autoReloadError: AutoReloadError | null
  recordingAccessState?: RecordingAccessState
  recordingGraceEndsAt?: number
}

interface BillingCheckCreditsRaw {
  plan: BillingPlanId
  tariffCredits: number
  extraCredits: number
}

export interface BillingCheckCredits {
  plan: BillingPlanId
  tariffCreditsLeft: number
  extraCredits: number
}

export interface BillingCheckoutRequest {
  lookupKey: BillingLookupKey
  successPath: string
  cancelPath: string
  toltReferral?: string
}

interface BillingCheckoutOptions {
  onRedirect?: () => void
}

export interface BillingChangePlanRequest {
  lookupKey: BillingLookupKey
}

export interface BillingChangePlanResponse {
  status: "active" | "scheduled"
  effectiveAt?: number
  prorationCents: number
}

interface BillingChangePlanResponseRaw extends Omit<
  BillingChangePlanResponse,
  "effectiveAt"
> {
  effectiveAt?: number | string | null
}

export interface BillingCancelResponse {
  effectiveAt: number
}

interface BillingCancelResponseRaw {
  effectiveAt: number | string | null
}

export type BillingReactivateResponse =
  | {
      status: "active"
    }
  | {
      needsCheckout: true
    }

interface BillingRedirectResponse {
  url: string
}

const redirectToUrl = (url: string) => {
  window.location.assign(url)
}

const normalizeRequiredTimestamp = (value?: number | string | null): number =>
  toUnixMilliseconds(value) ?? 0

const normalizeInvoice = (
  invoice: BillingSummaryInvoiceRaw,
): BillingSummaryInvoice => {
  return {
    ...invoice,
    descriptionKey: invoice.descriptionKey ?? undefined,
    descriptionParams: invoice.descriptionParams ?? undefined,
    date: normalizeRequiredTimestamp(invoice.date),
    periodEnd: toUnixMilliseconds(invoice.periodEnd),
    periodStart: toUnixMilliseconds(invoice.periodStart),
  }
}

const normalizeUsage = (data: BillingSummaryRaw): BillingUsageSnapshot => {
  return {
    tariffCreditsTotal: data.usage.totalTariffCredits,
    tariffCreditsLeft: data.tariffCredits,
    extraCredits: data.usage.extraCredits,
    resetsAt:
      toUnixMilliseconds(data.creditsRenewAt ?? data.currentPeriodEnd) ?? null,
    breakdown: {
      transcription: data.usage.spentToTranscription,
      summary: data.usage.spentToSummary,
      askAi: data.usage.spentToAskAi,
    },
  }
}

const normalizeProvider = (provider?: string | null): BillingProvider => {
  if (provider === "stripe" || provider === "apple" || provider === "google") {
    return provider
  }
  return ""
}

const isStoreManagedProvider = (provider: BillingProvider): boolean =>
  provider === "apple" || provider === "google"

const normalizeSummary = (data: BillingSummaryRaw): BillingSummary => {
  const provider = normalizeProvider(data.provider)
  const isStoreManaged = isStoreManagedProvider(provider)

  return {
    plan: data.plan ?? "free",
    interval: data.interval ?? "",
    status: data.status ?? "",
    provider,
    currentPeriodEnd: toUnixMilliseconds(data.currentPeriodEnd),
    creditsRenewAt:
      toUnixMilliseconds(data.creditsRenewAt ?? data.currentPeriodEnd) ?? null,
    cancelAtPeriodEnd: data.cancelAtPeriodEnd ?? false,
    pendingPlan: data.pendingPlan ?? "",
    pendingEffectiveAt: toUnixMilliseconds(data.pendingEffectiveAt) ?? null,
    manageBillingAvailable:
      !isStoreManaged && (data.manageBillingAvailable ?? false),
    recentInvoices: (data.recentInvoices ?? []).map(normalizeInvoice),
    usage: normalizeUsage(data),
    cardLastFour: isStoreManaged ? undefined : data.cardLastFour,
    cardBrand: isStoreManaged
      ? undefined
      : (data.cardBrand || "Card").toUpperCase(),
    cardExpMonth: isStoreManaged ? undefined : data.cardExpMonth,
    cardExpYear: isStoreManaged ? undefined : data.cardExpYear,
    cardLinkEmail: isStoreManaged ? undefined : data.cardLinkEmail,
    autoReloadError: isStoreManaged ? null : (data.autoReloadError ?? null),
    recordingAccessState: parseRecordingAccessState(data.recordingAccessState),
    recordingGraceEndsAt:
      data.recordingGraceEndsAt != null
        ? (toUnixMilliseconds(data.recordingGraceEndsAt) ?? undefined)
        : undefined,
  }
}

const normalizeCheckCredits = (
  data: BillingCheckCreditsRaw,
): BillingCheckCredits => ({
  plan: data.plan,
  tariffCreditsLeft: data.tariffCredits,
  extraCredits: data.extraCredits,
})

const normalizeChangePlanResponse = (
  data: BillingChangePlanResponseRaw,
): BillingChangePlanResponse => ({
  ...data,
  effectiveAt: toUnixMilliseconds(data.effectiveAt),
})

const normalizeCancelResponse = (
  data: BillingCancelResponseRaw,
): BillingCancelResponse => ({
  effectiveAt: normalizeRequiredTimestamp(data.effectiveAt),
})

async function getPlans(): Promise<BillingPlan[]> {
  const response = await httpClient.get<BillingPlansResponse>("/billing/plans")

  const raw = Array.isArray(response.data)
    ? response.data
    : (response.data.plans ?? [])

  const plans = raw.map(normalizePlan)

  return plans
}

async function getSummary(): Promise<BillingSummary> {
  const response = await httpClient.get<BillingSummaryRaw>("/billing/summary")
  return normalizeSummary(response.data)
}

async function checkCredits(): Promise<BillingCheckCredits> {
  const response = await httpClient.get<BillingCheckCreditsRaw>(
    "/billing/check-credits",
  )

  return normalizeCheckCredits(response.data)
}

async function checkout(
  request: BillingCheckoutRequest,
  options?: BillingCheckoutOptions,
): Promise<void> {
  options?.onRedirect?.()
  const payload: BillingCheckoutRequest = {
    ...request,
    ...(window.tolt_referral ? { toltReferral: window.tolt_referral } : {}),
  }
  const response = await httpClient.post<BillingRedirectResponse>(
    "/billing/checkout",
    payload,
  )

  redirectToUrl(response.data.url)
}

async function changePlan(
  request: BillingChangePlanRequest,
): Promise<BillingChangePlanResponse> {
  const response = await httpClient.post<BillingChangePlanResponseRaw>(
    "/billing/change-plan",
    request,
  )

  return normalizeChangePlanResponse(response.data)
}

async function cancel(): Promise<BillingCancelResponse> {
  const response =
    await httpClient.post<BillingCancelResponseRaw>("/billing/cancel")
  return normalizeCancelResponse(response.data)
}

async function reactivate(): Promise<BillingReactivateResponse> {
  const response = await httpClient.post<BillingReactivateResponse>(
    "/billing/reactivate",
  )

  return response.data
}

async function openPortal(request: { returnPath: string }): Promise<string> {
  const response = await httpClient.post<BillingRedirectResponse>(
    "/billing/portal",
    request,
  )

  return response.data.url
}

async function getCreditsPackPricing(): Promise<BillingCreditsPackPricingResponse> {
  const response = await httpClient.get<BillingCreditsPackPricingResponse>(
    "/billing/credits-pack/pricing",
  )

  return response.data
}

async function creditsPackCheckout(
  request: BillingCreditsPackCheckoutRequest,
  options?: BillingCheckoutOptions,
): Promise<void> {
  options?.onRedirect?.()
  const response = await httpClient.post<BillingRedirectResponse>(
    "/billing/credits-pack/checkout",
    request,
  )

  redirectToUrl(response.data.url)
}

export type BillingUsageBreakdownKey = "transcription" | "summary" | "askAi"

export interface RecordingStorageSnapshot {
  storageCapacityTotalBytes: number
  storageCapacityLeftBytes: number
  storageRecordingsBytes: number
  storageTrashBytes: number
  recordingCount: number
  trashCount: number
}

export interface BillingUsageSnapshot {
  tariffCreditsTotal: number
  tariffCreditsLeft: number
  extraCredits: number
  resetsAt: number | null
  breakdown: Partial<Record<BillingUsageBreakdownKey, number>> & {
    [key: string]: number
  }
}

export interface AutoReloadSettings {
  enabled: boolean
  threshold: number
  rechargeAmount: number
  monthlySpend: number
  error?: AutoReloadError
  autoReloadResetDate?: number
  managePaymentUrl?: string
}

export interface AutoReloadSettingsRaw extends Omit<
  AutoReloadSettings,
  "autoReloadResetDate"
> {
  autoReloadResetDate?: number | string | null
}

export type AutoReloadSettingsRequest = Pick<
  AutoReloadSettings,
  "threshold" | "rechargeAmount" | "monthlySpend"
>

export const normalizeAutoReloadSettings = (
  settings: AutoReloadSettingsRaw,
): AutoReloadSettings => ({
  ...settings,
  autoReloadResetDate: toUnixMilliseconds(settings.autoReloadResetDate),
})

async function getAutoReloadSettings(): Promise<AutoReloadSettings | null> {
  const response = await httpClient.get<AutoReloadSettingsRaw | null>(
    "/billing/auto-reload",
  )

  return response.data ? normalizeAutoReloadSettings(response.data) : null
}

async function saveAutoReloadSettings(
  settings: AutoReloadSettingsRequest,
): Promise<void> {
  await httpClient.post("/billing/auto-reload", settings)
}

async function deleteAutoReloadSettings(): Promise<void> {
  await httpClient.delete("/billing/auto-reload")
}

async function retryAutoReloadPayment(): Promise<AutoReloadSettings> {
  const response = await httpClient.post<AutoReloadSettingsRaw>(
    "/billing/auto-reload/retry",
  )
  return normalizeAutoReloadSettings(response.data)
}

export const billingApi = {
  getPlans,
  getSummary,
  checkCredits,
  checkout,
  changePlan,
  cancel,
  reactivate,
  openPortal,
  getCreditsPackPricing,
  creditsPackCheckout,
  getAutoReloadSettings,
  saveAutoReloadSettings,
  deleteAutoReloadSettings,
  retryAutoReloadPayment,
}
